/* eslint-disable no-console */

import { datadogRum } from './datadog-rum';
import type { DatadogFailureReason, DatadogOperationOptions } from './datadog-rum';
import * as React from 'react';
import { hasConsentedToAdvertising } from '@dx-ui/framework-marketing-utils';

/**
 * Set user information in Datadog RUM.
 * This should be called when user logs in or when user information is available.
 *
 * @param guestId - User ID (e.g., visitorId, memberId)
 * @param additionalAttributes - Additional user attributes (optional)
 */
export function setDatadogUser(guestId?: string, additionalAttributes?: Record<string, unknown>) {
  if (!datadogRum) {
    return;
  }

  const user: Record<string, unknown> = {
    ...additionalAttributes,
  };

  if (guestId) {
    user['name'] = guestId;
  }

  datadogRum.setUser(user);
}

/**
 * Add a custom action to Datadog RUM.
 * Use this to track user interactions like button clicks, form submissions, etc.
 *
 * @param name - Action name (e.g., 'button_click', 'form_submit')
 * @param context - Optional context object with additional data
 */
export function addDatadogAction(name: string, context?: Record<string, unknown>) {
  if (!datadogRum || !datadogRum.addAction) {
    return;
  }

  datadogRum.addAction(name, context);
}

/**
 * Add a custom error to Datadog RUM.
 * Use this to track errors that occur in your application.
 *
 * @param error - Error object or error message
 * @param context - Optional context object with additional data
 */
export function addDatadogError(error: Error | string, context?: Record<string, unknown>) {
  if (!datadogRum || !datadogRum.addError) {
    return;
  }

  datadogRum.addError(error, context);
}

/**
 * Set a global context property in Datadog RUM.
 * This property will be included in all events.
 *
 * @param key - Property key
 * @param value - Property value
 */
export function setDatadogGlobalContext(key: string, value: unknown) {
  if (!datadogRum) {
    return;
  }

  datadogRum.setGlobalContextProperty(key, value);
}

/**
 * Remove a global context property from Datadog RUM.
 *
 * @param key - Property key to remove
 */
export function removeDatadogGlobalContext(key: string) {
  if (!datadogRum) {
    return;
  }

  datadogRum.removeGlobalContextProperty(key);
}

/**
 * Custom hook to track page views in Datadog RUM.
 * Similar to useManualPageLogger for Dynatrace.
 *
 * @param viewName - View name (usually matches location.pathname)
 * @param context - Optional context object with additional data
 */
export const useDatadogPageView = (viewName: string, context?: Record<string, unknown>): void => {
  React.useEffect(() => {
    if (!datadogRum) {
      return;
    }

    // Set view name
    datadogRum.setViewName(viewName);

    // Add context if provided
    if (context) {
      Object.entries(context).forEach(([key, value]) => {
        datadogRum?.setGlobalContextProperty(key, value);
      });
    }
  }, [viewName, context]);
};

/**
 * Check if user has consented to tracking via Clarip.
 * This is useful for respecting user privacy preferences.
 *
 * @returns true if user has consented to advertising/tracking cookies, false otherwise
 */
export function hasConsentedToTracking(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    // Use the marketing-utils package which handles Clarip consent
    // This avoids type conflicts with claripCookieManager declarations
    return hasConsentedToAdvertising();
  } catch {
    console.warn('Datadog RUM: User has not consented to tracking. RUM will not be initialized.');
    return false;
  }
}

/**
 * Custom hook that tracks page visibility changes in Datadog RUM.
 * Fires a `page_visibility` action whenever the tab becomes visible or hidden.
 */
export function useDatadogPageVisibility(): void {
  React.useEffect(() => {
    const handleVisibilityChange = () => {
      addDatadogAction('page_visibility', { visibilityState: document.visibilityState });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
}

/**
 * Add a feature flag evaluation to Datadog RUM.
 * Use this to track feature flag states for debugging and monitoring.
 *
 * @param key - Feature flag key/name
 * @param value - Feature flag value (boolean, string, or number)
 */
export function addDatadogFeatureFlag(key: string, value: boolean | string | number) {
  if (!datadogRum || typeof datadogRum.addFeatureFlagEvaluation !== 'function') {
    return;
  }
  datadogRum?.addFeatureFlagEvaluation(key, value);
}

function getDatadogOperationMethod<T extends (...args: never[]) => void>(
  currentMethod: T | undefined,
  legacyMethod: T | undefined
): T | undefined {
  if (typeof currentMethod === 'function') {
    return currentMethod;
  }

  if (typeof legacyMethod === 'function') {
    return legacyMethod;
  }

  return undefined;
}

/**
 * Start a Datadog RUM operation for operations monitoring.
 *
 * @param name - Operation name (e.g., 'checkout', 'search')
 * @param options - Optional operationKey, context, and description
 */
export function startDatadogOperation(name: string, options?: DatadogOperationOptions) {
  const startOperation = getDatadogOperationMethod(
    datadogRum?.startOperation,
    datadogRum?.startFeatureOperation
  );

  if (!startOperation) {
    return;
  }

  startOperation(name, options);
}

/**
 * Mark a Datadog RUM operation as successful.
 *
 * @param name - Operation name matching a prior startDatadogOperation call
 * @param options - Optional operationKey, context, and description
 */
export function succeedDatadogOperation(name: string, options?: DatadogOperationOptions) {
  const succeedOperation = getDatadogOperationMethod(
    datadogRum?.succeedOperation,
    datadogRum?.succeedFeatureOperation
  );

  if (!succeedOperation) {
    return;
  }

  succeedOperation(name, options);
}

/**
 * Mark a Datadog RUM operation as failed.
 *
 * @param name - Operation name matching a prior startDatadogOperation call
 * @param failureReason - Reason for the failure ('error', 'abandoned', or 'other')
 * @param options - Optional operationKey, context, and description
 */
export function failDatadogOperation(
  name: string,
  failureReason: DatadogFailureReason,
  options?: DatadogOperationOptions
) {
  const failOperation = getDatadogOperationMethod(
    datadogRum?.failOperation,
    datadogRum?.failFeatureOperation
  );

  if (!failOperation) {
    return;
  }

  failOperation(name, failureReason, options);
}

/**
 * Initialize Datadog RUM with user consent check.
 * Only initializes if user has consented to tracking.
 * This should be called after DatadogScript has loaded.
 *
 * Note: This is typically handled automatically by DatadogScript,
 * but can be used for manual initialization if needed.
 */
export function initializeDatadogWithConsent() {
  if (!hasConsentedToTracking()) {
    console.warn('Datadog RUM: User has not consented to tracking. RUM will not be initialized.');
    return false;
  }

  // DatadogScript handles initialization automatically
  // This function is mainly for manual control if needed
  return true;
}
